import { gql } from '@apollo/client'
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  IconBuildingSkyscraper,
  IconUsers,
  IconTicket,
  IconMessage,
  IconActivity,
  IconCoin,
  IconHome,
} from '@tabler/icons-react'

import { Link, useParams } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'
import { Toaster } from '@redwoodjs/web/toast'

import { useAuth } from 'src/auth'
import { AvatarCacheProvider } from 'src/components/AvatarCacheProvider/AvatarCacheProvider'
import DomainAvatar from 'src/components/DomainAvatar/DomainAvatar'
import WorkspaceFinder from 'src/components/WorkspaceFinder/WorkspaceFinder'
import { internalUserEmails } from 'src/lib/gates'

const WORKSPACE_QUERY = gql`
  query WorkspaceQuery($id: String!) {
    workspace(id: $id) {
      id
      name
      domains {
        domain
      }
    }
  }
`

type AdminWorkspaceLayoutProps = {
  children?: React.ReactNode
}

const drawerWidth = 240

const containerSx = {
  display: 'flex',
  height: '100vh',
  width: '100%',
  bgcolor: 'background.default',
}

const drawerSx = {
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    bgcolor: 'background.paper',
    borderRight: 1,
    borderColor: 'divider',
  },
}

const mainSx = {
  flexGrow: 1,
  bgcolor: 'background.default',
  height: '100vh',
  overflow: 'auto',
}

const listItemButtonSx = {
  borderRadius: 1,
  mx: 1,
  '&:hover': {
    bgcolor: 'action.hover',
  },
  '&.active': {
    bgcolor: 'action.selected',
  },
}

const AdminWorkspaceLayout = ({ children }: AdminWorkspaceLayoutProps) => {
  const { currentUser } = useAuth()
  const { workspaceId } = useParams()

  // Fetch workspace data
  const { data: workspaceData } = useQuery(WORKSPACE_QUERY, {
    variables: { id: workspaceId },
    skip: !workspaceId,
  })

  const currentWorkspace = workspaceData?.workspace

  // Check if user has admin access
  const hasAdminAccess = internalUserEmails.includes(currentUser?.email)

  if (!hasAdminAccess) {
    return (
      <Box sx={mainSx}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            variant="h4"
            gutterBottom
          >
            Access Denied
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            You do not have permission to access the admin area.
          </Typography>
        </Box>
      </Box>
    )
  }

  const workspaceNavItems = [
    {
      icon: <IconHome size={20} />,
      label: 'Workspace Overview',
      to: `/admin/workspace/${workspaceId}`,
    },
    {
      icon: <IconBuildingSkyscraper size={20} />,
      label: 'Organizations',
      to: `/admin/orgs/${workspaceId}`,
    },
    {
      icon: <IconUsers size={20} />,
      label: 'People',
      to: `/admin/people/${workspaceId}`,
    },
    {
      icon: <IconCoin size={20} />,
      label: 'Opportunities',
      to: `/admin/opportunities/${workspaceId}`,
    },
    {
      icon: <IconTicket size={20} />,
      label: 'Invites',
      to: `/admin/invites/${workspaceId}`,
    },
    {
      icon: <IconActivity size={20} />,
      label: 'Actions',
      to: `/admin/actions/${workspaceId}`,
    },
    {
      icon: <IconMessage size={20} />,
      label: 'Slack (Workspace)',
      to: `/admin/slack/${workspaceId}`,
    },
  ]

  const drawer = (
    <>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: 'primary.main' }}
        >
          Day.ai Admin
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Workspace Administration
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <WorkspaceFinder currentWorkspaceId={workspaceId} />
        {currentWorkspace && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentWorkspace.domains?.length > 0 &&
              currentWorkspace.domains[0]?.domain && (
                <DomainAvatar
                  domain={currentWorkspace.domains[0].domain}
                  size={24}
                  borderRadius={100}
                />
              )}
            <Typography
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {currentWorkspace.name}
            </Typography>
          </Box>
        )}
      </Box>
      <Divider />
      <List sx={{ px: 1, py: 2 }}>
        {workspaceNavItems.map((item) => (
          <ListItem
            key={item.label}
            disablePadding
            sx={{ mb: 0.5 }}
          >
            <ListItemButton
              component={Link}
              to={item.to}
              sx={listItemButtonSx}
              className={
                window.location.pathname === item.to ? 'active' : undefined
              }
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 1, py: 2 }}>
        <ListItem
          disablePadding
          sx={{ mb: 0.5 }}
        >
          <ListItemButton
            component={Link}
            to="/admin"
            sx={listItemButtonSx}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <IconUsers size={20} />
            </ListItemIcon>
            <ListItemText
              primary="Global Admin"
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/today"
            sx={listItemButtonSx}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <IconHome size={20} />
            </ListItemIcon>
            <ListItemText
              primary="Back to App"
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
      <Box sx={{ mt: 'auto', p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Logged in as
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500 }}
        >
          {currentUser?.email}
        </Typography>
      </Box>
    </>
  )

  return (
    <AvatarCacheProvider>
      <Box sx={containerSx}>
        <Toaster toastOptions={{ className: 'rw-toast', duration: 6000 }} />
        <Drawer
          variant="permanent"
          sx={{
            ...drawerSx,
            '& .MuiDrawer-paper': {
              ...drawerSx['& .MuiDrawer-paper'],
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Box
          component="main"
          sx={mainSx}
        >
          {children}
        </Box>
      </Box>
    </AvatarCacheProvider>
  )
}

export default AdminWorkspaceLayout
